// @givmo/app-charity — "is this element's lane at least N pixels wide?", measured off the element
// itself (CPA-WEB40 · the container-driven statement switch).
//
// The statement table's desktop composition used to switch on a VIEWPORT query (`min-width: 80rem`),
// which was only ever a proxy for the lane the table actually has. Under shell contract v3 every
// page wears the sidebar, so the same viewport hands the table a narrower lane — and a proxy that
// answers for the window instead of the frame composes the desktop set into a lane that cannot hold
// it. The CSS half of the switch is a container query on the table's frame; this is the JS half,
// keyed on the same element and the same threshold, so the measured column order and the CSS
// variants flip together.
//
// ⚠ UNMEASURED ⇒ TRUE. jsdom and a server render measure nothing (a zero-width rect, no
// `ResizeObserver`), and the answer there is the desktop composition — exactly what the media-query
// hook answered without `matchMedia`, so every existing pin keeps its meaning. A REAL zero-width
// lane does not exist for a mounted table.

import { useLayoutEffect, useState, type RefObject } from 'react';

export function useLaneAtLeast(ref: RefObject<HTMLElement | null>, minPx: number): boolean {
  const [atLeast, setAtLeast] = useState(true);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const read = (width: number) => {
      if (width > 0) setAtLeast(width >= minPx);
    };
    // Synchronously, before paint: the first frame composes for the lane it has, never for a
    // guess it then corrects.
    read(element.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) read(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, minPx]);

  return atLeast;
}
